import axios from 'axios'
import type { AxiosError } from 'axios'
import { saveRedirectTarget } from '@/lib/auth/redirectTarget'
import { reportServerReachable, reportServerUnreachable } from '@/lib/offline/connectivity'
import { clearReplayToken, saveReplayToken } from '@/lib/offline/outboxDb'
import { clearPersistedQueryCache } from '@/lib/offline/queryPersist'

// clear the expired session and send the user to login, first stashing the page they
// were on so they can be returned there after re-authenticating
const redirectToLogin = () => {
  localStorage.removeItem('fp3_auth_token')
  localStorage.removeItem('fp3_user')
  // the session is over, so the offline copies go with it: the persisted cache holds this
  // user's account, category, group and template names, and the mirrored token would let a
  // background sync keep using their credentials. this path is a hard navigation, so it
  // cannot rely on AuthProvider.logout having run.
  void clearPersistedQueryCache()
  void clearReplayToken()
  saveRedirectTarget(window.location.pathname + window.location.search + window.location.hash)
  window.location.href = '/login'
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // send cookies with requests
})

// feed every request outcome to the connectivity signal the offline UI reads. a reply of
// any status proves the backend is up; a failure with no response at all is what "offline"
// actually means here, covering both a phone with no signal and a backend that is down.
apiClient.interceptors.response.use(
  (response) => {
    reportServerReachable()
    return response
  },
  (error: AxiosError) => {
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      // an aborted request says nothing about the server
    } else if (error.response) {
      reportServerReachable()
    } else {
      reportServerUnreachable()
    }
    return Promise.reject(error)
  }
)

// track if we're currently refreshing the token to prevent multiple simultaneous refresh requests
let isRefreshing = false
// queue of failed requests waiting for token refresh
let failedRequestsQueue: Array<{
  resolve: (token: string) => void
  reject: (error: Error) => void
}> = []

// process all queued requests after successful token refresh
const processQueue = (error: Error | null, token: string | null = null) => {
  failedRequestsQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token!)
    }
  })
  failedRequestsQueue = []
}

// request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('fp3_auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// response interceptor for automatic token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any & {
      _retry?: boolean
    }

    // if error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      const token = localStorage.getItem('fp3_auth_token')

      // if no token, this is likely a login failure - let it be handled by the mutation
      if (!token) {
        return Promise.reject(error)
      }

      // don't retry refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        // refresh failed, clear auth and redirect to login
        redirectToLogin()
        return Promise.reject(error)
      }

      // mark this request as retried
      originalRequest._retry = true

      if (isRefreshing) {
        // if already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      isRefreshing = true

      try {
        // try to refresh the token
        const response = await apiClient.post('/auth/refresh')
        const { access_token } = response.data

        // save new access token and notify SSE listeners
        localStorage.setItem('fp3_auth_token', access_token)
        // keep the service worker's copy in step. access tokens live ~15 minutes, so a
        // mirror written only at login is stale almost immediately and the closed-app
        // background sync would 401 on every queued expense.
        void saveReplayToken(access_token)
        window.dispatchEvent(new CustomEvent('fp3:token-refreshed'))

        // update authorization header
        originalRequest.headers.Authorization = `Bearer ${access_token}`

        // process queued requests with new token
        processQueue(null, access_token)

        // retry the original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        // refresh failed, clear auth and redirect to login
        processQueue(refreshError as Error, null)
        redirectToLogin()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)
